import { useState } from "react";
import { cn } from "@/lib/utils";
import { useCampaigns } from "@/hooks/use-campaigns";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/skeleton";
import { CampaignWizard } from "./campaign-wizard";
import { Send } from "lucide-react";

type StatusFilter = "all" | "draft" | "scheduled" | "sent";

/** Email campaigns page — list + create wizard. */
export default function CampaignsPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [showWizard, setShowWizard] = useState(false);

  const apiStatus = statusFilter === "all" ? undefined : statusFilter;
  const { campaigns, isLoading, error, refetch } = useCampaigns(apiStatus);

  return (
    <div className="density-gap space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Campaigns</h1>
        <button
          onClick={() => setShowWizard(true)}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          + New Campaign
        </button>
      </div>

      {/* Status filter */}
      <div className="flex gap-1 rounded-lg border border-border p-1">
        {(["all", "draft", "scheduled", "sent"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={cn(
              "flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              statusFilter === s ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent",
            )}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-md border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error} <button onClick={refetch} className="underline">Retry</button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-md" />
          ))}
        </div>
      ) : campaigns.length === 0 ? (
        <EmptyState
          icon={<Send className="h-8 w-8" />}
          title="No campaigns yet"
          description="Create your first email campaign to reach your audience."
          actionLabel="Create campaign"
          onAction={() => setShowWizard(true)}
        />
      ) : (
        <div className="divide-y divide-border rounded-lg border border-border" data-reveal>
          {campaigns.map((campaign) => (
            <div key={campaign.id} className="stagger-item flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-sm font-medium text-foreground">{campaign.name}</p>
                <div className="flex gap-2 text-xs text-muted-foreground">
                  {campaign.settings?.subjectOverride && <span>{campaign.settings.subjectOverride}</span>}
                  <StatusBadge status={campaign.status} />
                </div>
              </div>
              <div className="text-right text-xs text-muted-foreground">
                {campaign.completedAt
                  ? `Sent ${new Date(campaign.completedAt).toLocaleDateString()}`
                  : campaign.scheduledAt
                    ? `Scheduled ${new Date(campaign.scheduledAt).toLocaleDateString()}`
                    : `Draft`}
              </div>
            </div>
          ))}
        </div>
      )}

      {showWizard && (
        <CampaignWizard
          onClose={() => setShowWizard(false)}
          onCreated={() => { setShowWizard(false); refetch(); }}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    draft: "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]",
    scheduled: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
    sending: "bg-[var(--color-warning)]/10 text-[var(--color-warning)]",
    sent: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
    failed: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]",
  };
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", colors[status] ?? "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]")}>
      {status}
    </span>
  );
}
